
import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent
} from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [featured_media.alt]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

/**
 * Sticky Add To Cart Component
 * Always visible on product pages.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber'
  ];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Cache main product form button
    const productForm = this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]'
      );
    }

    // Always show sticky bar immediately
    this.#showStickyBar();

    // Product variant updates
    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    // Cart events
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    // Quantity updates
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Handles sticky add to cart click
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';

    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) return;

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.classList.add('fly-to-cart--sticky');

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product variant selection
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) return;

    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant
        } = detail;

        if (
          productId &&
          productId !== this.dataset.productId
        ) return;

        const newStickyAddToCart = html.querySelector(
          'sticky-add-to-cart'
        );

        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector(
          '[ref="stickyBar"]'
        );

        if (!newStickyBar) return;

        // Preserve always-visible state
        const currentStuck = 'true';

        const variantAvailable =
          newStickyAddToCart.dataset.variantAvailable;

        // Update sticky bar content
        morph(
          this.refs.stickyBar,
          newStickyBar,
          { childrenOnly: true }
        );

        // Keep sticky bar visible after morph
        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.#isStuck = true;

        this.dataset.variantAvailable = variantAvailable;

        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Refresh main product button reference
        const productForm = this.#getProductForm();

        if (productForm) {
          this.#targetAddToCartButton =
            productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );
        }
      });
  };

  /** @type {boolean} */
  #isStuck = true;

  /**
   * Handles unavailable variants
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector(
      '.sticky-add-to-cart__variant'
    );

    const productId = this.dataset.productId;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add completion
   * @param {CartLinesUpdateEvent | CartErrorEvent} event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity updates
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;

    this.#updateButtonText();
  };

  /**
   * Always show sticky bar
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Keep sticky bar visible
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Gets product form
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;

    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace(
      'shopify-section-',
      ''
    );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets initial quantity
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(
      this.dataset.initialQuantity || '1'
    ) || 1;

    this.#updateButtonText();
  }

  /**
   * Updates quantity display
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber
    } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}
